<template>
  <div id="app">
    <div class="flex justify-center mb-4">
      <div class="sm:flex border-b border-gray-200">
        <div class="flex space-x-8">
          <a
            v-for="kitchen in kitchens"
            :key="kitchen.id"
            href="#"
            @click.prevent="activeKitchenTab = kitchen.id"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium leading-5 transition duration-150 ease-in-out"
            :class="{
              'border-blue-500 text-blue-600 font-bold border-b-4':
                activeKitchenTab === kitchen.id,
              'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300':
                activeKitchenTab !== kitchen.id,
            }"
          >
            {{ kitchen.name }}
          </a>
        </div>
      </div>
    </div>
    <div class="flex justify-center mb-4 px-4">
      <v-select
        v-model="selectedTables"
        itemid="id"
        label="name"
        placeholder="Masa Filtreleme.."
        :options="allTables"
        class="w-full mt-2"
        :clearable="true"
        multiple
      ></v-select>
    </div>
    <div class="flex justify-center">
      <div
        class="min-h-screen flex overflow-x-scroll py-12"
        style="padding-top: 0rem !important"
      >
        <!-- Bekleniyor Kolonu -->
        <div
          class="bg-gray-100 rounded-lg px-3 py-3 column-width rounded mr-4"
          key="waiting"
        >
          <p
            class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
          >
            Bekleniyor
          </p>
          <draggable
            :list="filteredWaitingOrders"
            :animation="200"
            ghost-class="ghost-card"
            group="orders"
            style="min-height: 400px"
            @change="handleStatusChange($event, 0)"
          >
            <template>
              <div v-for="order in filteredWaitingOrders" :key="order.id">
                <div
                  class="mt-3 cursor-move bg-white shadow rounded px-3 pt-3 pb-5 warning"
                >
                  <div class="flex flex-col justify-between">
                    <p
                      class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
                    >
                      {{ order.productName }}
                    </p>
                    <div class="flex flex-wrap gap-1 text-red-600">
                      <div
                        class="text-xs rounded-lg bg-black bg-opacity-25 px-2"
                        v-for="extra in order.extras"
                        :key="extra.id"
                      >
                        x{{ extra.quantity }} {{ extra.name }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="flex mt-4 justify-between items-center"
                    v-if="order.note"
                  >
                    <span class="text-sm text-gray-600">
                      <vs-chip
                        ><vs-avatar icon="chat_bubble_outline" />Note:
                        {{ order.note }}
                      </vs-chip>
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip
                        ><vs-avatar icon="home" />Masa:
                        {{ order.tableName }}</vs-chip
                      >
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip
                        ><vs-avatar />Kullanıcı: {{ order.userName }}</vs-chip
                      >
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>

        <!-- Hazırlanıyor Kolonu -->
        <div
          class="bg-gray-100 rounded-lg px-3 py-3 column-width rounded mr-4"
          key="preparing"
        >
          <p
            class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
          >
            Hazırlanıyor
          </p>
          <draggable
            :list="filteredPreparingOrders"
            :animation="200"
            ghost-class="ghost-card"
            group="orders"
            style="min-height: 400px"
            @change="handleStatusChange($event, 1)"
          >
            <template>
              <div v-for="order in filteredPreparingOrders" :key="order.id">
                <div
                  class="mt-3 cursor-move bg-white shadow rounded px-3 pt-3 pb-5"
                >
                  <div class="flex flex-col justify-between">
                    <p
                      class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
                    >
                      {{ order.productName }}
                    </p>
                    <div class="flex flex-wrap gap-1 text-red-600">
                      <div
                        class="text-xs rounded-lg bg-black bg-opacity-25 px-2"
                        v-for="extra in order.extras"
                        :key="extra.id"
                      >
                        x{{ extra.quantity }} {{ extra.name }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="flex mt-4 justify-between items-center"
                    v-if="order.note"
                  >
                    <span class="text-sm text-red-600">
                      <vs-chip class="text-red-600"
                        ><vs-avatar icon="chat_bubble_outline" />Note:
                        {{ order.note }}
                      </vs-chip>
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip
                        ><vs-avatar icon="home" />Masa:
                        {{ order.tableName }}</vs-chip
                      >
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip>
                        <vs-avatar />Kullanıcı: {{ order.userName }}</vs-chip
                      >
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>

        <!-- Tamamlandı Kolonu -->
        <div
          class="bg-gray-100 rounded-lg px-3 py-3 column-width rounded mr-4"
          key="completed"
        >
          <p
            class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
          >
            Tamamlandı
          </p>
          <draggable
            :list="filteredCompletedOrders"
            :animation="200"
            ghost-class="ghost-card"
            group="orders"
            style="min-height: 400px"
            @change="handleStatusChange($event, 2)"
          >
            <template>
              <div v-for="order in filteredCompletedOrders" :key="order.id">
                <div
                  class="mt-3 cursor-move bg-white shadow rounded px-3 pt-3 pb-5"
                >
                  <div class="flex flex-col justify-between">
                    <p
                      class="text-gray-700 font-semibold font-sans tracking-wide text-sm"
                    >
                      {{ order.productName }}
                    </p>
                    <div class="flex flex-wrap gap-1 text-red-600">
                      <div
                        class="text-xs rounded-lg bg-black bg-opacity-25 px-2"
                        v-for="extra in order.extras"
                        :key="extra.id"
                      >
                        x{{ extra.quantity }} {{ extra.name }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="flex mt-4 justify-between items-center"
                    v-if="order.note"
                  >
                    <span class="text-sm text-gray-600">
                      <vs-chip
                        ><vs-avatar icon="chat_bubble_outline" />Note:
                        {{ order.note }}
                      </vs-chip>
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip>
                        <vs-avatar icon="home" />Masa:
                        {{ order.tableName }}</vs-chip
                      >
                    </span>
                  </div>
                  <div class="flex mt-4 justify-between items-center">
                    <span class="text-sm text-gray-600">
                      <vs-chip>
                        <vs-avatar />Kullanıcı: {{ order.userName }}</vs-chip
                      >
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>
    </div>
    <vs-popup :active.sync="isNotePopupActive" title="Sipariş Notu">
      <p>{{ currentNote }}</p>
    </vs-popup>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { getCarts, setBranchCart, setStatus } from "@/api/Cart";
import { getAllKitchen } from "@/api/Kitchen";
export default {
  name: "Kitchen",
  components: {
    draggable,
  },
  data() {
    return {
      waitingOrders: [],
      preparingOrders: [],
      completedOrders: [],
      carts: [],
      cartEntries: [],
      pollingInterval: null,
      isNotePopupActive: false,
      currentNote: "",
      selectedTables: [],
      kitchens: [],
      activeKitchenTab: null,
      filteredWaitingOrders: [],
      filteredPreparingOrders: [],
      filteredCompletedOrders: [],
    };
  },
  watch: {
    activeKitchenTab: {
      handler() {
        this.filteredWaitingOrders = this.filterOrdersBySelectedTables(
          this.waitingOrders
        );
        this.filteredPreparingOrders = this.filterOrdersBySelectedTables(
          this.preparingOrders
        );
        this.filteredCompletedOrders = this.filterOrdersBySelectedTables(
          this.completedOrders
        );
      },
      deep: true,
    },
    waitingOrders: {
      handler(value) {
        this.filteredWaitingOrders = this.filterOrdersBySelectedTables(value);
      },
      deep: true,
    },
    preparingOrders: {
      handler(value) {
        this.filteredPreparingOrders = this.filterOrdersBySelectedTables(value);
      },
      deep: true,
    },
    completedOrders: {
      handler(value) {
        this.filteredCompletedOrders = this.filterOrdersBySelectedTables(value);
      },
      deep: true,
    },
  },
  computed: {
    areas() {
      return this.$store.getters["selfservice/getAreas"];
    },
    allTables() {
      // Tüm masaları areas'dan çıkart
      return this.areas.flatMap((area) => area.buttons);
    },
  },
  methods: {
    async handleStatusChange(event, newStatus) {
      console.log(event);
      if (event.added || event.moved) {
        const order = event.added ? event.added.element : event.moved.element;
        // Kartın statüsünü güncelle
        /*  order.status = newStatus; */

        // Burada API'ye status değişikliğini gönderebilirsiniz
        await this.updateOrderStatus(order.id, newStatus);
        await getCarts();
      }
    },
    async updateOrderStatus(orderId, newStatus) {
      try {
        // İlgili cart'ı bul
        const carts = this.$store.getters["selfservice/getCarts"];
        const cart = carts.find((c) =>
          c.cartEntries.some((entry) => entry.id === orderId)
        );

        if (cart) {
          // Cart entry'yi güncelle
          /* const entry = cart.cartEntries.find((e) => e.id === orderId);
          entry.status = newStatus; */

          // Cart'ı güncelle
          await setStatus({
            status: newStatus,
            id: orderId,
            tableId: cart.tableId,
          });
          this.$store.dispatch("selfservice/updateToCart", cart);

          console.log(
            "Order status updated:",
            orderId,
            "New status:",
            newStatus
          );
        }
      } catch (error) {
        console.error("Error updating order status:", error);
        this.$vs.notify({
          color: "danger",
          title: "Hata",
          text: "Sipariş durumu güncellenirken bir hata oluştu",
        });
      }
    },
    async loadKitchens() {
      this.kitchens = (await getAllKitchen()).data;
      this.kitchens.unshift({
        id: null,
        name: "Tüm Mutfaklar",
      });
    },
    async getCarts() {
      try {
        let carts = await getCarts();
        const parsedCarts = carts.data.map((item) => JSON.parse(item));

        if (parsedCarts.length > 0) {
          this.$store.dispatch("selfservice/setToCart", parsedCarts);
        }

        this.carts = this.$store.getters["selfservice/getCarts"].filter(
          (cart) => cart !== null
        );

        // Cart entries'leri tableName ile birlikte oluştur
        this.cartEntries = this.carts.flatMap((cart) => {
          // Cart'ın tableId'sine göre areas içinden table'ı bul
          const table = this.areas
            .flatMap((area) => area.buttons)
            .find((table) => table.id === cart.tableId);

          // Cart'ın entries'lerini map'le ve her birine tableName ekle
          return (cart.cartEntries || []).map((entry) => ({
            ...entry,
            tableName: table ? table.name : "Bilinmeyen Masa",
          }));
        });
        console.log("cartEntries", this.cartEntries);
        // Update arrays based on status
        this.waitingOrders = this.cartEntries.filter(
          (entry) => !entry.status || entry.status === 0
        );
        console.log(this.waitingOrders);
        this.preparingOrders = this.cartEntries.filter(
          (entry) => entry.status === 1
        );

        this.completedOrders = this.cartEntries.filter(
          (entry) => entry.status === 2
        );

        console.log("Fetched carts:", this.carts);
        console.log("Cart entries:", this.cartEntries);
      } catch (error) {
        console.error("Error fetching carts:", error);
        this.$vs.notify({
          color: "danger",
          title: "Hata",
          text: "Siparişler yüklenirken bir hata oluştu",
        });
      }
    },
    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.getCarts();
      }, 30000); // 10 saniye
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    },
    showNotePopup(note) {
      console.log(note);
      this.currentNote = note;
      this.isNotePopupActive = true;
    },
    filterOrdersBySelectedTables(orders) {
      let filteredOrders = orders;

      // Önce mutfak filtrelemesi yap
      if (
        this.activeKitchenTab &&
        this.activeKitchenTab !== null &&
        this.activeKitchenTab !== 0
      ) {
        filteredOrders = filteredOrders.filter(
          (order) => order.kitchenId === this.activeKitchenTab
        );
      }

      // Sonra masa filtrelemesi yap
      if (this.selectedTables.length) {
        filteredOrders = filteredOrders.filter((order) =>
          this.selectedTables.some((table) => table.name === order.tableName)
        );
      }

      return filteredOrders;
    },
  },
  async created() {
    this.getCarts();
    this.startPolling();
    await this.loadKitchens();
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.column-width {
  min-width: 320px;
  width: 320px;
}

.ghost-card {
  opacity: 0.5;
  background: #f7fafc;
  border: 1px solid #4299e1;
}

.warning {
  animation: none;
  color: red;
}
@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>
